import { Injectable } from '@angular/core';
import { UserRegisterRequest, VerifyAccountRequest, VerifyEmailRequest } from './user-aux.model';
import { catchError, map, Observable, switchMap } from 'rxjs';
import { failStatus, StatusRequest, successStatus } from '../../../../core/models/request-status.model';
import { OccEndpointsService, TranslationService, UserIdService } from '@spartacus/core';
import { HttpClient } from '@angular/common/http';
import { OptionsRequest } from '../../../../core/models/others.model';
import { Option } from '../../../../core/models/option.model';

@Injectable({
  providedIn: 'root',
})
export class UserAuxConnector {
  constructor(
    private http: HttpClient,
    private occEndPointService: OccEndpointsService,
    private translationService: TranslationService,
    private userIdService: UserIdService,
  ) {}

  register(user: UserRegisterRequest): Observable<StatusRequest> {
    const url = this.occEndPointService.buildUrl('register');

    return this.http.post(url, user).pipe(
			switchMap(() =>
				this.translationService.translate('register.requestCreatedSuccessfully').pipe(
					map(successMessage => ({
						...successStatus,
						message: successMessage,
					}))
				)
			),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            message: textError,
            ...failStatus,
          })),
        );
      }),
    );
  }

  getStateRegions(isoCode: string): Observable<OptionsRequest> {
    const url = this.occEndPointService.buildUrl('stateRegions', { urlParams: { isoCode } });

    return this.http.get(url).pipe(
      map((response: any) => {
        const options: Option[] = (response?.regions || []).map((region: any) => ({
          code: region.isocode,
          label: region.name,
        }));

        return {
          options,
          ...successStatus,
        };
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            message: textError,
            ...failStatus,
          })),
        );
      }),
    );
  }

  getBusinessTypes(): Observable<OptionsRequest> {
    const url = this.occEndPointService.buildUrl('businessTypes', { urlParams: { userId: 'userId' } });

    return this.http.get(url).pipe(
      map((response: any) => {
        return {
          options: response || [],
          ...successStatus,
        };
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            message: textError,
            ...failStatus,
          })),
        );
      }),
    );
  }

  getProductCategories(): Observable<OptionsRequest> {
    const url = this.occEndPointService.buildUrl('productCategories');

    return this.http.get(url).pipe(
      map((response: any) => {
        return {
          options: response || [],
          ...successStatus,
        };
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            message: textError,
            ...failStatus,
          })),
        );
      }),
    );
  }

  verifyAccount(accountNumber: string): Observable<VerifyAccountRequest> {
    const url = this.occEndPointService.buildUrl('validateAccount', { urlParams: { accountNumber } });

    return this.http.post(url, { accountNumber }).pipe(
      map((reponse: any) => {
        return {
          ...successStatus,
          hasAccount: reponse.success,
        };
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            message: textError,
            ...failStatus,
          })),
        );
      }),
    );
  }

  verifyEmail(email: string): Observable<VerifyEmailRequest> {
    const url = this.occEndPointService.buildUrl('validateEmail', { queryParams: { email } });

    return this.http.get(url).pipe(
      map((reponse: any) => {
        return {
          ...successStatus,
          status: reponse.status,
        };
      }),
      catchError(() => {
        return this.translationService.translate('generic.error').pipe(
          map(textError => ({
            ...failStatus,
            message: textError,
          })),
        );
      }),
    );
  }

  requestRole(role: string): Observable<StatusRequest> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('requestRole', { urlParams: { userId, role } });

        return this.http.post(url, {}).pipe(
          map(() => successStatus),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }

  requestAccount(accountNumber: string, reason: string): Observable<VerifyAccountRequest> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap(userId => {
        const url = this.occEndPointService.buildUrl('requestAccount', { urlParams: { userId, accountNumber } });

        return this.http.post(url, { accountNumber, reason }).pipe(
					switchMap((response: any) =>
						this.translationService.translate('register.requestCreatedSuccessfully').pipe(
							map(successMessage => ({
								...successStatus,
								message: successMessage,
								hasAccount: response.success,
							}))
						)
					),
          catchError(() => {
            return this.translationService.translate('generic.error').pipe(
              map(textError => ({
                message: textError,
                ...failStatus,
              })),
            );
          }),
        );
      }),
    );
  }
}
